import React, { useEffect, useState } from 'react';
import Check from 'src/models/Check';
import Dialog from 'src/components/common/Dialog';
import CheckDueDateExtension from 'src/models/CheckDueDateExtension';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import Button from 'src/components/controls/forms/redesign/FormButton';
import { useForm } from 'react-hook-form';
import useFetch from 'use-http';
import Card from 'src/components/common/Card.redesign';
import Invoice from 'src/models/leasing/Invoice';
import moment from 'moment';

interface IManageChecksDialogStateProps {
    clientId: number;
    projectId: number;
    dialogName: string;
    currentCheck: Check;
    currentInvoice: Invoice;
    hideDialog: (reset: boolean) => void;
}

const ManageChecksDialog = (props: IManageChecksDialogStateProps) => {
    const defaultValues: CheckDueDateExtension = {
        id: 0,
        checkId: props.currentCheck?.id,
        invoiceId: props.currentInvoice?.id,
        originalDraftDueDate: props.currentInvoice?.draftDueDate ? props.currentInvoice.draftDueDate : null,
        originalInvoiceDueDate: props.currentInvoice?.invoiceDueDate ? props.currentInvoice.invoiceDueDate : null,
        newDraftDueDate: props.currentInvoice?.draftDueDate ? props.currentInvoice.draftDueDate : null,
        newInvoiceDueDate: props.currentInvoice?.invoiceDueDate ? props.currentInvoice.invoiceDueDate : null,
        reason: '',
        createdDate: null
    };

    const [previousExtensions, setPreviousExtensions] = useState<CheckDueDateExtension[]>([]);
    const [dueDateExtension, setDueDateExtension] = useState<CheckDueDateExtension>(defaultValues);
    const { register, handleSubmit, reset, errors } = useForm();
    const { get, post, response } = useFetch();

    const onSubmit = async (extension: CheckDueDateExtension) => {
        extension = formatData(extension);

        await post(`v1.0/clients/${props.clientId}/projects/${props.projectId}/check/check-due-date`, extension);

        if (response.ok) {
            onCloseDialog();
            props.hideDialog(true);
        }
    };

    const loadExtensions = async () => {
        if (props.currentCheck?.id > 0) {
            let result: CheckDueDateExtension[] = await get(`v1.0/clients/${props.clientId}/projects/${props.projectId}/check/check-due-date/${props.currentCheck.id}`);
            result.forEach((ex, key) => {
                ex = formatData(ex);
                result[key] = ex;
            });

            if (response.ok) {
                setPreviousExtensions(result);
            }
        }
    };

    useEffect(() => {
        loadExtensions();
        setDueDateExtension(defaultValues);
    }, [props.currentCheck]);

    useEffect(() => {
        reset(dueDateExtension);
    }, [dueDateExtension]);

    const onCloseDialog = () => {
        setPreviousExtensions(undefined);
        setDueDateExtension(undefined);
    };

    const formatData = (data: CheckDueDateExtension): CheckDueDateExtension => {
        data.reason = data.reason ? data.reason : '';

        data.originalDraftDueDate = data.originalDraftDueDate
        ? (moment(data.originalDraftDueDate).format('YYYY-MM-DD') as unknown as Date)
        : null;
        data.originalInvoiceDueDate = data.originalInvoiceDueDate
        ? (moment(data.originalInvoiceDueDate).format('YYYY-MM-DD') as unknown as Date)
        : null;
        data.newDraftDueDate = data.newDraftDueDate
        ? (moment(data.newDraftDueDate).format('YYYY-MM-DD') as unknown as Date)
        : null;
        data.newInvoiceDueDate = data.newInvoiceDueDate
        ? (moment(data.newInvoiceDueDate).format('YYYY-MM-DD') as unknown as Date)
        : null;

        // Automatic updates
        data.createdDate = data.createdDate
            ? (moment(data.createdDate).format('YYYY-MM-DD') as unknown as Date)
            : (moment().format('YYYY-MM-DD') as unknown as Date);

        data.checkId = props.currentCheck?.id ? props.currentCheck.id : 0;
        data.invoiceId = props.currentInvoice?.id ? props.currentInvoice.id : 0;

        return data;
    };
    
    const displayPreviousExtensions = () => {
        if (previousExtensions?.length > 0) {
            return (
                previousExtensions.map((pe, key) => {
                    return (
                        <Card className="small-input" newContext={true} key={key}>
                            <div style={{ display: 'flex' }}>
                                <div className="mr-2" style={{ width: '100%' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <div style={{ padding: '1rem', width: '15%' }}>
                                            <div>Date Extended</div>
                                            <div>{pe.createdDate ? pe.createdDate : 'N/A'}</div>
                                        </div>
                                        <div style={{ padding: '1rem', width: '15%' }}>
                                            <div>Original Draft Due Date</div>
                                            <div>{pe.originalDraftDueDate ? pe.originalDraftDueDate : 'N/A'}</div>
                                        </div>
                                        <div style={{ padding: '1rem', width: '15%' }}>
                                            <div>New Draft Due Date</div>
                                            <div>{pe.newDraftDueDate ? pe.newDraftDueDate : 'N/A'}</div>
                                        </div>
                                        <div style={{ padding: '1rem', width: '15%' }}>
                                            <div>Original Invoice Due Date</div>
                                            <div>{pe.originalInvoiceDueDate ? pe.originalInvoiceDueDate : 'N/A'}</div>
                                        </div>
                                        <div style={{ padding: '1rem', width: '15%' }}>
                                            <div>New Invoice Due Date</div>
                                            <div>{pe.newInvoiceDueDate ? pe.newInvoiceDueDate : 'N/A'}</div>
                                        </div>
                                        <div style={{ padding: '1rem', width: '25%' }}>
                                            <div>Reason</div>
                                            <div>{pe.reason ? pe.reason : 'N/A'}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    );
                })
            );
        } else {
            return <div>No records available.</div>;
        }
    };

    return (
        <Dialog dialogName={props.dialogName} title={`Extend Due Date`} bodyStyle={{ width: '75%' }}>
            <div className="mb-1 mt-1">
                {displayPreviousExtensions()}
            </div>
            <div className="mb-1 mt-1">
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div style={{ display: 'flex'}}>
                        <div className="mr-2" style={{ width: '100%' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <RHFInputBox name="originalDraftDueDate" label="Original Due Date" register={register} errors={errors} type="date" disabled={true} />
                                <RHFInputBox name="newDraftDueDate" label="New Due Date" register={register} errors={errors} type="date" />
                                <RHFInputBox name="originalInvoiceDueDate" label="Original Invoice Due Date" register={register} errors={errors} type="date" disabled={true} />
                                <RHFInputBox name="newInvoiceDueDate" label="New Invoice Due Date" register={register} errors={errors} type="date" />
                            </div>
                            <div className="form-row">
                                <RHFInputBox name="reason" label="Reason" register={register} errors={errors} style={{ width: '100%' }} />
                            </div>
                            <div>
                                <Button text="Save Changes" intent="primary" type="submit" />
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </Dialog>
    );
};

export default ManageChecksDialog;


// WEBPACK FOOTER //
// ./src/components/invoicing/components/ExtendCheckDueDateDialog.tsx